import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { InquiryForm } from "@/components/InquiryForm";
import {
  Sparkles, ShieldCheck, Clock, BadgeIndianRupee, GraduationCap,
  Briefcase, Stethoscope, ArrowRight, CheckCircle2, Building2,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.55, ease: "easeOut" } }),
};

const stats = [
  { num: "5,400+", label: "Students Trained" },
  { num: "82%", label: "Placement Rate" },
  { num: "120+", label: "Hiring Partners" },
  { num: "4.8★", label: "Avg. Rating" },
];

const highlights = [
  { icon: Clock, t: "45 Days Offline/Online", d: "Attend in-person at BIOMAC or join live online — recordings available either way." },
  { icon: BadgeIndianRupee, t: "Free Seat Reservation", d: "Reserve a seat with zero upfront payment. Our counsellor will walk you through fees." },
  { icon: GraduationCap, t: "Any Graduate / Diploma", d: "B.Sc, B.E., B.Tech, B.Pharm, BPT, Diploma — all welcome." },
  { icon: Briefcase, t: "Placement Assistance", d: "Mock interviews + introductions to 120+ employers." },
];

export const Home = () => {
  const [course, setCourse] = useState(null);
  useEffect(() => {
    api.get("/courses/medical-devices-15-day").then((r) => setCourse(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section className="relative bg-grid overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            className="lg:col-span-7"
            initial="hidden" animate="show" variants={fadeUp}
          >
            <span className="eyebrow" data-testid="hero-eyebrow">
              <Sparkles size={12} className="inline mr-1 -mt-0.5" />
              Job-oriented · Medical Devices · Career Rooting
            </span>
            <h1
              className="mt-5 font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-[#0A3B32] leading-[1.05]"
              data-testid="hero-title"
            >
              Root your career in the<br />
              <span className="text-[#C85A40]">medical devices</span> industry.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              BIOMAC's 45-day intensive online program turns graduates and diploma students into
              hire-ready biomedical professionals — at a fee anyone can afford.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/courses/medical-devices-15-day" className="btn-primary" data-testid="hero-enroll-btn">
                Reserve Free Seat <ArrowRight size={18} />
              </Link>
              <Link to="/courses" className="btn-outline" data-testid="hero-explore-btn">
                Explore Course
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  custom={i} variants={fadeUp} initial="hidden" animate="show"
                  className="bg-white border border-slate-200/80 rounded-xl p-4"
                  data-testid={`hero-stat-${i}`}
                >
                  <p className="font-heading text-2xl font-bold text-[#0A3B32]">{s.num}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="relative">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_biomac-job-ready/artifacts/8a5fa788c4880918_engineer.jpeg"
                alt="BIOMAC service engineer servicing a fully-automatic clinical chemistry analyzer"
                className="rounded-2xl shadow-2xl object-cover w-full h-[460px]"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-slate-100 max-w-[260px]" data-testid="hero-floating-card">
                <div className="flex items-center gap-2 text-[#C85A40] font-semibold text-xs uppercase tracking-wider">
                  <ShieldCheck size={16} /> Trusted institute
                </div>
                <p className="mt-2 font-heading text-lg font-semibold text-[#0A3B32]">
                  India's most affordable biomedical bootcamp
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- HIGHLIGHTS BENTO ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="highlights-section">
        <div className="grid md:grid-cols-12 gap-6">
          {highlights.map((h, i) => (
            <motion.div
              key={h.t}
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              custom={i}
              className={`card-soft md:col-span-3`}
              data-testid={`highlight-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#0A3B32]/10 text-[#0A3B32] flex items-center justify-center mb-5">
                <h.icon strokeWidth={1.6} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#0A3B32]">{h.t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{h.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- ABOUT STRIP ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="about-strip">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <img
              src="https://images.pexels.com/photos/35216547/pexels-photo-35216547.jpeg"
              alt="Indian students learning PCB repair in electronics lab"
              className="rounded-2xl w-full h-[420px] object-cover"
            />
          </div>
          <div className="lg:col-span-6">
            <span className="eyebrow">About BIOMAC</span>
            <h2 className="mt-4 font-heading text-4xl font-semibold text-[#0A3B32] tracking-tight">
              We don't just teach devices.<br />We make you employable.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              BIOMAC is a coaching center built specifically for the medical devices industry.
              Our trainers come from GE, Philips, Siemens and India's top hospitals — so the
              curriculum is built around what employers actually want.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Live classes by working industry professionals",
                "Hands-on demo of 30+ critical hospital devices",
                "Resume & interview preparation included",
                "Access to recorded sessions during the program",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="text-[#C85A40] mt-0.5" size={20} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-semibold text-[#0A3B32] hover:text-[#C85A40]" data-testid="about-strip-link">
              Read our story <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- COURSE OFFER ---------- */}
      <section className="bg-[#0A3B32] py-24 text-white" data-testid="course-offer-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="text-[#C85A40] text-xs font-semibold tracking-[0.2em] uppercase">
              Flagship Program
            </span>
            <h2 className="mt-4 font-heading text-5xl font-semibold tracking-tight">
              45-Day Medical Devices<br />Career Rooting Course
            </h2>
            <p className="mt-5 text-slate-300 text-lg max-w-2xl leading-relaxed">
              {course?.description ||
                "An intensive online program designed to turn freshers into job-ready biomedical professionals."}
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="text-xs uppercase tracking-wider text-slate-400">Duration</p>
                <p className="mt-2 font-heading text-2xl font-semibold">45 Days</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="text-xs uppercase tracking-wider text-slate-400">Mode</p>
                <p className="mt-2 font-heading text-2xl font-semibold">Online</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="text-xs uppercase tracking-wider text-slate-400">Reservation</p>
                <p className="mt-2 font-heading text-2xl font-semibold text-[#C85A40]">Free</p>
                <p className="mt-1 text-[11px] font-semibold text-slate-400">Limited seats</p>
              </div>
            </div>
            <Link to="/courses/medical-devices-15-day" className="btn-primary mt-10" data-testid="course-offer-cta">
              View Full Syllabus <ArrowRight size={18} />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-slate-900 rounded-2xl p-2 sm:p-3">
              <div className="px-4 pt-4 pb-2">
                <p className="eyebrow">Quick Enquiry</p>
                <p className="font-heading text-xl font-semibold text-[#0A3B32] mt-1">
                  Get a call back in 24 hours
                </p>
              </div>
              <div className="p-2">
                <InquiryForm compact />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- PARTNERS ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="partners-section">
        <div className="text-center">
          <span className="eyebrow">Where our students go</span>
          <h2 className="font-heading mt-3 text-4xl font-semibold text-[#0A3B32]">Hiring Partners</h2>
        </div>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {(course?.placement_partners || ["GE Healthcare", "Philips", "Siemens", "Trivitron", "BPL", "Apollo Hospitals", "Fortis", "Manipal"]).map((p) => (
            <div key={p} className="card-soft text-center" data-testid={`partner-${p}`}>
              <Building2 className="mx-auto text-[#0A3B32]" />
              <p className="mt-3 font-heading font-semibold text-slate-800">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="card-soft text-center py-16">
          <Stethoscope className="mx-auto text-[#C85A40]" size={40} />
          <h2 className="font-heading mt-4 text-4xl font-semibold text-[#0A3B32]">
            One decision can change your career.
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Seats are limited per batch. Join the next BIOMAC cohort and start your medical
            device career in just 45 days.
          </p>
          <Link to="/courses/medical-devices-15-day" className="btn-primary mt-8" data-testid="final-cta-btn">
            Reserve Free Seat <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};
